/** src/presentation/pages/DashboardPage.tsx */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from '@presentation/components/layout/Header';
import { AppDrawer } from '@presentation/components/layout/AppDrawer/AppDrawer';
import { useAuthContext } from '@presentation/context/AuthContext';
import { routes } from '@presentation/router';

import { Form } from '@/presentation/components/Forms/Form/Form';
import { useSimple } from '@/presentation/hooks/useSimple';
import { useCongrupo } from '@/presentation/hooks/useCongrupo';
import { useEquipo } from '@/presentation/hooks/useEquipo';
import { useIncidente } from '@/presentation/hooks/useIncidente';
import { isValidPhoneNumber } from '@/shared/utils/validators';


export default function NuevoIncidente() {

  const { data: grupos } = useSimple(
    'luc_ubicaciones_grupo'
  );
  const { data: tipos } = useSimple(
    'luc_tipo_incidente'
  );
  const {
    data: ubicaciones,
    loadByGroup,
  } = useCongrupo();
  const tipoOptions = tipos.map(
    (tipo) => ({
      value: tipo.id,
      label: tipo.nombre
    })
  )

  const grupoOptions = grupos.map(
    (grupo) => ({
      value: grupo.id,
      label: grupo.nombre,
    })
  );
  const ubicacionOptions =
    ubicaciones.map((u) => ({
      value: u.id,
      label: u.nombre,
    }));
  const {
    data: equipos,
    loadAll: loadEquipos,
  } = useEquipo();
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();
  const { id } = useParams();
  const editing = Boolean(id) && id !== '0';

  const {
    incidente,
    loadById,
  } = useIncidente();

  const equipoOptions = useMemo(() => {
    const options = equipos.map((equipo) => ({
      value: equipo.id,
      label: equipo.equipo,
    }));

    if (
      editing &&
      incidente?.equipo &&
      !options.some(
        (option) => option.value === incidente.id_equipo
      )
    ) {
      options.unshift({
        value: incidente.id_equipo,
        label: incidente.equipo,
      });
    }

    return options;
  }, [editing, equipos, incidente?.equipo]);

  const initialValues = useMemo(() => {
    if (!incidente) return undefined;

    return {
      tipo: incidente.id_tipo,
      equipo: incidente.equipo,
      prioridad: incidente.prioridad,
      contador: incidente.contador,
      contacto: incidente.contacto,
      telefono: incidente.telefono,
    };
  }, [incidente]);

  useEffect(() => {
    if (editing && id) {
      loadById(Number(id));
    }
  }, [editing, id, loadById]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setIsDrawerOpen(false);
    navigate(routes.login, { replace: true });
  };

  const { create } = useIncidente();
  const { update } = useIncidente();


  const handleSubmit = async (
    values: Record<string, unknown>
  ) => {
    const nuevoIncidente = {
      tipo: Number(values.tipo),
      equipo: String(values.equipo),
      prioridad: Number(values.prioridad),
      contador: Number(values.contador),
      contacto: String(values.contacto),
      telefono: Number(values.telefono),
      usuario: Number(user?.id ?? 0),
    };

    const incidenteGuardado = editing && incidente
      ? await update(incidente.id.toString(), nuevoIncidente)
      : await create(nuevoIncidente);
    console.log('Incidente guardado:', incidenteGuardado.id);

    navigate(`/incidente/${incidenteGuardado.id}`, { replace: true });
  };




  return (
    <div>
      <Header title={
        editing
          ? 'Editar Incidente'
          : 'Nuevo Incidente'
      } onMenuClick={handleOpenDrawer} />

      <AppDrawer
        isOpen={isDrawerOpen}
        user={user}
        onClose={handleCloseDrawer}
        onLogout={handleLogout}
      />

      <main style={{ padding: '24px' }}>
        <Form
          fields={[
            {
              name: 'tipo',
              label: 'Tipo de Incidente',
              type: 'select',
              options: tipoOptions
            },
            {
              name: 'grupo',
              label: 'Grupo',
              type: 'select',
              options: grupoOptions,
              optional: true
            },
            {
              name: 'ubicacion',
              label: 'Ubicación',
              type: 'select',
              options: ubicacionOptions,
              optional: true
            },
            {
              name: 'equipo',
              label: 'Equipo',
              type: 'select',
              options: equipoOptions
            },
            {
              name: 'prioridad',
              label: 'Prioridad',
              type: 'select',
              options: [
                {
                  value: 1,
                  label: 'Baja',
                },
                {
                  value: 2,
                  label: 'Media',
                },
                {
                  value: 3,
                  label: 'Alta',
                },
              ],
            },
            {
              name: 'contador',
              label: 'Contador',
              type: 'number'
            },
            {
              name: 'contacto',
              label: 'Nombre del Contacto',
              type: 'text'
            },
            {
              name: 'telefono',
              label: 'Telefono',
              type: 'number',
              validation: (value) => {
                
                if (!isValidPhoneNumber(String(value))) {
                  return 'Escriba Número Válido';
                }

                return null;
              }
            }
          ]}
          onFieldChange={(name, value) => {
            if (name === 'grupo') {
              loadByGroup(
                'luc_ubicaciones',
                value as string
              );
            }
            if (name === 'ubicacion') {
              loadEquipos({
                ubicacion: Number(value),
              });
            }
          }}
          initialValues={initialValues}
          submitLabel={
            editing
              ? 'Guardar cambios'
              : 'Crear Incidente'
          }
          onSubmit={handleSubmit}
        />

      </main>


    </div>
  );
}
